import React, { useCallback } from 'react';
import {
    Button,
    Modal,
    Heading,
    InfoFilled,
    Paragraph,
    Flex,
} from '@landr/maestro';
import { usePurchaseControllerContext } from 'contexts/PurchaseControllerContext/PurchaseControllerContext';
import { Trans } from '@lingui/macro';
import { CONFIG } from 'utils/config';
import { useExecutionEnvironmentContext } from 'contexts/ExecutionEnvironmentContext/ExecutionEnvironmentContext';
import { ModalCloseHandler, ModalProps } from '../Modal.types';

interface PlanPausedModalContentProps extends ModalProps {
    handleConfirm: () => void;
}

export const _PlanPausedModal = ({
    handleConfirm,
    isOpen,
    handleClose,
}: PlanPausedModalContentProps) => (
    <Modal isOpen={isOpen} onClose={handleClose} size="sm" isCentered>
        <Modal.Header />
        <Modal.Content>
            <Modal.Section>
                <Flex
                    flexDirection={'column'}
                    justifyContent={'center'}
                    alignItems={'center'}
                >
                    <InfoFilled size="xl" color="neutral.400" />
                    <Heading size="xl" textAlign={'center'}>
                        <Trans>Your subscription is paused</Trans>
                    </Heading>
                    <Paragraph size="md" contrast="low" textAlign={'center'}>
                        <Trans>
                            You paused your subscription and no longer have
                            access to this feature. Resume your subscription to
                            retrieve access.
                        </Trans>
                    </Paragraph>
                    <Button
                        variant={'primary'}
                        size={'md'}
                        isRounded
                        onClick={handleConfirm}
                    >
                        <Trans>Manage subscription</Trans>
                    </Button>
                </Flex>
            </Modal.Section>
        </Modal.Content>
        <Modal.Footer />
    </Modal>
);

export const usePlanPausedModal = ({
    isDAWPlugin,
    cancelPurchase,
    handleClose,
}: {
    isDAWPlugin: boolean;
    cancelPurchase: () => void;
    handleClose: ModalCloseHandler;
}) => {
    const handleClosePlanPausedModal = useCallback(() => {
        handleClose();
        cancelPurchase();
    }, [handleClose, cancelPurchase]);

    const handleConfirm = useCallback(() => {
        handleClose();
        cancelPurchase();
        const target = isDAWPlugin ? '_blank' : '_self';

        window.open(`${CONFIG.VITE_ACCOUNT_PLANS_URL}`, target);
    }, [handleClose, cancelPurchase, isDAWPlugin]);

    return { handleClosePlanPausedModal, handleConfirm };
};

// Todo: https://mixgenius.atlassian.net/browse/MAS-5773
const PlanPausedModal: React.FC<ModalProps> = ({ isOpen, handleClose }) => {
    const { isDAWPlugin } = useExecutionEnvironmentContext();
    const { cancelPurchase } = usePurchaseControllerContext();
    const { handleClosePlanPausedModal, handleConfirm } = usePlanPausedModal({
        isDAWPlugin,
        cancelPurchase,
        handleClose,
    });

    return (
        <_PlanPausedModal
            isOpen={isOpen}
            handleClose={handleClosePlanPausedModal}
            handleConfirm={handleConfirm}
        />
    );
};

export default PlanPausedModal;
